import { getColors } from "@/shared/utils/ganttColors";
import { getMomentTz } from "@dexteel/mesf-core";
import TableViewIcon from "@mui/icons-material/TableView";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Stack } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { InspectionReportRow } from "../../models/InspectionReport";

const moment = getMomentTz();

export const SectionCell = (
	params: ICellRendererParams<InspectionReportRow>,
) => (
	<Box display="flex" alignItems="center" gap={1}>
		<Box
			sx={{
				width: 10,
				height: 10,
				borderRadius: "50%",
				backgroundColor: getColors(params.data?.SectionId ?? 0).fill,
				flexShrink: 0,
			}}
		/>
		{params.value}
	</Box>
);

const countCellStyle = (value: number, color: string) => ({
	color: value > 0 ? color : "#9e9e9e",
	fontWeight: value > 0 ? 700 : 400,
});

export const useTableData = ({
	viewDrawing,
	viewInManager,
}: {
	viewDrawing: (sheet: InspectionReportRow) => void;
	viewInManager: (sheet: InspectionReportRow) => void;
}) => {
	const columnDefs: ColDef<InspectionReportRow>[] = [
		{
			field: "SectionName",
			headerName: "Section",
			cellRenderer: "agGroupCellRenderer",
			cellRendererParams: { innerRenderer: SectionCell },
			flex: 1.2,
		},
		{
			field: "Rev",
			headerName: "Rev",
			flex: 0.8,
			valueFormatter: (params) => `Rev ${params.value}`,
		},
		{
			field: "EventId",
			headerName: "Event",
			flex: 0.8,
			valueFormatter: (params) => (params.value ? `#${params.value}` : "-"),
		},
		{
			field: "Heats",
			headerName: "Heats",
			flex: 3,
			tooltipField: "Heats",
			valueFormatter: (params) => params.value ?? "-",
			cellStyle: {
				display: "flex",
				justifyContent: "flex-start",
			},
		},
		{
			field: "ProductionDate",
			headerName: "Shift",
			minWidth: 130,
			flex: 1,
			valueFormatter: (params) => {
				if (!params.value) return "";

				const shift = `${params.data?.Shift ?? ""}${
					params.data?.Crew ? `-${params.data.Crew}` : ""
				}`;

				return `${moment.utc(params.value).format("MM/DD/YY")} ${shift}`.trim();
			},
		},
		{
			field: "InspectorName",
			headerName: "Inspector",
			flex: 1.3,
			valueFormatter: (params) => params.value ?? "-",
			cellStyle: {
				display: "flex",
				justifyContent: "flex-start",
			},
		},
		{
			field: "ReadingCount",
			headerName: "Readings",
			flex: 0.8,
			type: "rightAligned",
			valueFormatter: (params) =>
				params.data && params.data.ReadingCount < params.data.SheetReadingCount
					? `${params.value} of ${params.data.SheetReadingCount}`
					: `${params.value}`,
		},
		{
			field: "NoGoCount",
			headerName: "No-Go",
			flex: 0.8,
			type: "rightAligned",
			cellStyle: (params) => countCellStyle(params.value ?? 0, "#d32f2f"),
		},
		{
			field: "DefectCount",
			headerName: "Defects",
			flex: 0.8,
			type: "rightAligned",
			tooltipField: "SurfaceDefects",
			cellStyle: (params) => countCellStyle(params.value ?? 0, "#ed6c02"),
		},
		{
			field: "Comments",
			headerName: "Comments",
			flex: 3,
			tooltipField: "Comments",
			valueFormatter: (params) => params.value ?? "-",
			cellStyle: {
				display: "flex",
				justifyContent: "flex-start",
			},
		},
		{
			headerName: "",
			colId: "actions",
			pinned: "right",
			sortable: false,
			filter: false,
			resizable: false,
			maxWidth: 92,
			minWidth: 92,
			cellRenderer: (params: ICellRendererParams<InspectionReportRow>) =>
				params.data ? (
					<Stack
						direction="row"
						justifyContent="center"
						alignItems="center"
						height="100%"
					>
						<IconButton
							size="small"
							color="primary"
							disabled={!params.data.DrawingPath}
							title={
								params.data.DrawingPath ? "View drawing" : "No profile drawing"
							}
							onClick={() => viewDrawing(params.data as InspectionReportRow)}
						>
							<VisibilityOutlinedIcon fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							color="primary"
							title="View in Manager"
							onClick={() => viewInManager(params.data as InspectionReportRow)}
						>
							<TableViewIcon fontSize="small" />
						</IconButton>
					</Stack>
				) : null,
		},
	];

	return { columnDefs };
};
